import { useState, useEffect } from "react";
import { Copy, Check, ExternalLink } from "lucide-react";

const KIND_OPTIONS = [
  { label: "Dynamic", bit: 1 },
  { label: "Overlay", bit: 2 },
  { label: "Banner", bit: 4 },
  { label: "Static", bit: 8 },
];

const TYPE_OPTIONS = [
  { value: "vast", label: "VAST" },
  { value: "rtb", label: "RTB" },
];

const DEFAULT_FORM = {
  name: "",
  alias: "",
  types: ["vast"],
  kinds: 0,
  cpm: 0,
  allowTransit: false,
  settings: { transitBufferMs: "", issueViewerCookie: false },
};

const API_PREFIX = "/api";
const API_BASE = import.meta.env?.VITE_API_URL ?? API_PREFIX;

// Region endpoints for publisher serving/reporting URLs. Add a region by
// appending one entry here — the UI renders a copyable link per region
// automatically. `origin: null` = the current dashboard host (GLOBAL keeps
// today's behavior); a string origin points the links at that regional host.
const REGIONS = [
  { label: "GLOBAL", origin: null },
  { label: "AMERICAS", origin: "https://us.app.adlywise.com" },
  // e.g. { label: "EUROPE", origin: "https://eu.app.adlywise.com" },
];

const buildServingUrl = (protocol, alias, token, origin) => {
  // Publisher serving URLs live at the HOST ROOT, never under /api: nginx routes
  // /vast and /rtb straight to the serving (app) pool, whereas /api/* goes to the
  // single api replica. Always strip a trailing /api (both the bare "/api" default
  // AND a full "https://host/api" base) so the copyable URL is host-root
  // /vast|/rtb — the canonical path publishers should point their players at.
  let base = API_BASE.replace(/\/+$/, "");
  if (base.endsWith(API_PREFIX)) {
    base = base.slice(0, -API_PREFIX.length);
  }
  const path = `${base}/${protocol}/${alias}/${token}`;
  try {
    return new URL(path, origin || window.location.origin).href;
  } catch {
    return path;
  }
};

const slugify = (value) =>
  value
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9-]/g, "");

const fromProvider = (p) => ({
  name: p.name ?? "",
  alias: p.alias ?? "",
  types: Array.isArray(p.types) && p.types.length ? p.types : ["vast"],
  kinds: p.kinds ?? 0,
  cpm: p.cpm ?? 0,
  allowTransit: p.allowTransit ?? false,
  settings: {
    transitBufferMs:
      p.settings?.transitBufferMs != null
        ? String(p.settings.transitBufferMs)
        : "",
    issueViewerCookie: Boolean(p.settings?.issueViewerCookie),
  },
});

function ServingUrl({ label, url }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      window.prompt("Copy this URL", url);
    }
  };

  return (
    <div className="flex items-center gap-2">
      <span className="min-w-[72px] text-xs font-bold uppercase text-(--text-muted)">
        {label}
      </span>
      <code className="flex-1 truncate rounded-(--radius-sm) bg-(--surface-2) px-2 py-1.5 text-xs text-(--text-primary)">
        {url}
      </code>
      <button
        type="button"
        onClick={handleCopy}
        className="inline-flex items-center justify-center rounded-(--radius-sm) border border-(--border-subtle) bg-(--surface-2) p-1.5 text-(--text-secondary) transition-colors hover:bg-(--surface-muted)"
        title="Copy URL"
        aria-label={`Copy ${label} URL`}
      >
        {copied ? (
          <Check size={15} className="text-(--status-success)" />
        ) : (
          <Copy size={15} />
        )}
      </button>
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center justify-center rounded-(--radius-sm) border border-(--border-subtle) bg-(--surface-2) p-1.5 text-(--text-secondary) transition-colors hover:bg-(--surface-muted)"
        title="Open URL"
        aria-label={`Open ${label} URL`}
      >
        <ExternalLink size={15} />
      </a>
    </div>
  );
}

// The <script> a publisher pastes into their site. Same host-root base as the
// serving URLs — /pixel goes to the serving pool, not the single api replica.
const buildPixelSnippet = (alias, token, origin) => {
  const tagUrl = buildServingUrl("pixel", alias, `${token}.js`, origin);
  return `<!-- Adlywise pixel -->\n<script async src="${tagUrl}"></script>`;
};

// The same endpoint, pasted two ways. As a bare <img> it is the whole install
// for a page that cannot run scripts; inside <noscript> it is the fallback that
// catches the script option's JavaScript-less visitors.
const buildPixelImage = (alias, token, origin) => {
  const gifUrl = buildServingUrl("pixel", alias, `${token}.gif`, origin);
  return `<!-- Adlywise pixel (no JavaScript) -->\n<img src="${gifUrl}" width="1" height="1" alt="" style="display:none" />`;
};

const buildPixelNoscript = (alias, token, origin) => {
  const gifUrl = buildServingUrl("pixel", alias, `${token}.gif`, origin);
  return `<noscript><img src="${gifUrl}" width="1" height="1" alt="" style="display:none" /></noscript>`;
};

// The two ways to install the pixel. They are not tiers of the same thing: the
// script collects the browser, the image collects only the request, and the
// image is the only option in an environment with no JavaScript at all.
const PIXEL_MODES = [
  {
    value: "script",
    label: "Script",
    hint: "Full collection — page, device and engagement. Needs JavaScript.",
  },
  {
    value: "image",
    label: "Image pixel",
    hint: "Request data only. Works with no JavaScript at all.",
  },
];

// A multi-line snippet with its own copy button. Distinct from ServingUrl,
// which is a single-line URL row: a snippet is pasted into HTML, so it is shown
// as a block and must survive copying with its newlines intact.
function CodeSnippet({ label, code }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      window.prompt("Copy this snippet", code);
    }
  };

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center justify-between gap-2">
        <span className="text-[11px] font-bold uppercase tracking-wide text-(--text-muted)">
          {label}
        </span>
        <button
          type="button"
          onClick={handleCopy}
          className="inline-flex items-center gap-1.5 rounded-(--radius-sm) border border-(--border-subtle) bg-(--surface-2) px-2 py-1 text-xs text-(--text-secondary) transition-colors hover:bg-(--surface-muted)"
          title="Copy snippet"
          aria-label={`Copy ${label} snippet`}
        >
          {copied ? (
            <>
              <Check size={13} className="text-(--status-success)" /> Copied
            </>
          ) : (
            <>
              <Copy size={13} /> Copy
            </>
          )}
        </button>
      </div>
      <pre className="overflow-x-auto rounded-(--radius-sm) bg-(--surface-2) px-3 py-2 text-xs leading-relaxed text-(--text-primary)">
        <code>{code}</code>
      </pre>
    </div>
  );
}

// The publisher-facing install block: pick how the pixel is embedded, copy that
// option's snippet. The script option additionally offers the two things a
// publisher only needs if they ask — a no-JavaScript fallback and the consent
// hook their CMP can call.
function PixelEmbed({ alias, token }) {
  const [region, setRegion] = useState(REGIONS[0].label);
  const [mode, setMode] = useState(PIXEL_MODES[0].value);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const origin =
    REGIONS.find((r) => r.label === region)?.origin ?? REGIONS[0].origin;
  const activeMode =
    PIXEL_MODES.find((m) => m.value === mode) ?? PIXEL_MODES[0];

  return (
    <div className="flex flex-col gap-3">
      {REGIONS.length > 1 && (
        <label className="flex items-center gap-2 text-xs text-(--text-muted)">
          Region
          <select
            value={region}
            onChange={(e) => setRegion(e.target.value)}
            className="rounded-(--radius-sm) border border-(--border-subtle) bg-(--surface-2) px-2 py-1 text-xs text-(--text-primary)"
          >
            {REGIONS.map((r) => (
              <option key={r.label} value={r.label}>
                {r.label}
              </option>
            ))}
          </select>
        </label>
      )}

      <div
        role="radiogroup"
        aria-label="Pixel install method"
        className="inline-flex self-start rounded-(--radius-sm) border border-(--border-subtle) bg-(--surface-2) p-0.5"
      >
        {PIXEL_MODES.map((m) => (
          <button
            key={m.value}
            type="button"
            role="radio"
            aria-checked={mode === m.value}
            onClick={() => setMode(m.value)}
            className={`rounded-(--radius-sm) px-3 py-1 text-xs transition-colors ${
              mode === m.value
                ? "bg-(--surface-muted) font-semibold text-(--text-primary)"
                : "text-(--text-secondary) hover:text-(--text-primary)"
            }`}
          >
            {m.label}
          </button>
        ))}
      </div>

      <p className="form-help-text">{activeMode.hint}</p>

      {mode === "script" ? (
        <>
          <CodeSnippet
            label="Paste before </head>"
            code={buildPixelSnippet(alias, token, origin)}
          />

          <button
            type="button"
            onClick={() => setShowAdvanced((v) => !v)}
            className="self-start text-xs text-(--text-secondary) underline decoration-dotted underline-offset-2 hover:text-(--text-primary)"
          >
            {showAdvanced ? "Hide" : "Show"} fallback &amp; consent options
          </button>

          {showAdvanced && (
            <div className="flex flex-col gap-3">
              <CodeSnippet
                label="No-JavaScript fallback (optional)"
                code={buildPixelNoscript(alias, token, origin)}
              />
              <CodeSnippet
                label="Suppress from a consent manager"
                code={`window.adlywise = window.adlywise || [];\nwindow.adlywise.push(["consent", false]);`}
              />
              <p className="form-help-text">
                Push <code>[&quot;consent&quot;, false]</code> before or after
                the tag loads, or add <code>data-consent=&quot;deny&quot;</code>{" "}
                to the script tag. Either stops collection.
              </p>
            </div>
          )}
        </>
      ) : (
        <>
          <CodeSnippet
            label="Paste anywhere in the page"
            code={buildPixelImage(alias, token, origin)}
          />
          <p className="form-help-text">
            Collects only what the image request itself carries: IP, user agent,
            language, the browser&apos;s client hints, and the page URL from the
            Referer header. No screen, timezone, fingerprint or engagement data
            — those need the script. The device id is resolved the same way, so
            these visitors still join the devices used for capping and
            targeting.
          </p>
          <p className="form-help-text">
            Where the page sends no Referer (email, AMP,{" "}
            <code>Referrer-Policy: no-referrer</code>), append{" "}
            <code>?u=&lt;page-url&gt;</code> to the image URL. Any other query
            parameter is ignored, so a cache-buster is safe to add.
          </p>
          <p className="form-help-text">
            An image cannot read a consent manager, so there is no runtime
            switch: gate it where the page is rendered and emit the tag only
            once consent allows it. Use one option per page — to cover both
            cases, keep the script and add the image inside{" "}
            <code>&lt;noscript&gt;</code>, or each page view is counted twice.
          </p>
        </>
      )}
    </div>
  );
}

// A protocol's publisher URL, rendered as one copyable row per region (REGIONS).
function ServingUrlGroup({ title, protocol, alias, token }) {
  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-[11px] font-bold uppercase tracking-wide text-(--text-muted)">
        {title}
      </span>
      <div className="flex flex-col gap-2">
        {REGIONS.map((region) => (
          <ServingUrl
            key={region.label}
            label={region.label}
            url={buildServingUrl(protocol, alias, token, region.origin)}
          />
        ))}
      </div>
    </div>
  );
}

export default function ProviderForm({ initialProvider, onSubmit, onCancel }) {
  const [form, setForm] = useState(() =>
    initialProvider ? fromProvider(initialProvider) : { ...DEFAULT_FORM },
  );
  const [error, setError] = useState("");

  useEffect(() => {
    setForm(
      initialProvider ? fromProvider(initialProvider) : { ...DEFAULT_FORM },
    );
    setError("");
  }, [initialProvider]);

  const toggleKind = (bit) => {
    setForm((prev) => ({ ...prev, kinds: prev.kinds ^ bit }));
  };

  const toggleType = (value) => {
    setForm((prev) => {
      const nextTypes = prev.types.includes(value)
        ? prev.types.filter((t) => t !== value)
        : [...prev.types, value];
      return {
        ...prev,
        types: nextTypes,
        allowTransit: !nextTypes.includes("rtb") ? false : prev.allowTransit,
      };
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      setError("Name is required");
      return;
    }
    if (form.types.length === 0) {
      setError("Select at least one type");
      return;
    }
    setError("");
    const hasRtb = form.types.includes("rtb");
    const hasVast = form.types.includes("vast");
    onSubmit({
      name: form.name.trim(),
      alias: form.alias.trim() || null,
      types: form.types,
      kinds: form.kinds,
      cpm: Number(form.cpm) || 0,
      allowTransit: hasRtb ? form.allowTransit : false,
      settings: {
        transitBufferMs:
          hasRtb && form.settings.transitBufferMs !== ""
            ? Number(form.settings.transitBufferMs)
            : null,
        issueViewerCookie: hasVast
          ? Boolean(form.settings.issueViewerCookie)
          : false,
      },
    });
  };

  const savedAlias = initialProvider?.alias ?? "";
  const savedToken = initialProvider?.secretToken ?? "";
  const savedTypes = Array.isArray(initialProvider?.types)
    ? initialProvider.types
    : [];
  const canShowUrls = Boolean(savedAlias && savedToken);
  const showVastUrl = canShowUrls && savedTypes.includes("vast");
  const showRtbUrl = canShowUrls && savedTypes.includes("rtb");

  return (
    <form
      className="builder-form"
      style={{ gap: "0.5rem" }}
      onSubmit={handleSubmit}
    >
      {error && (
        <div className="form-error" style={{ marginBottom: "1rem" }}>
          {error}
        </div>
      )}

      <div className="form-group-modal">
        <label htmlFor="provider-name">Name *</label>
        <input
          id="provider-name"
          value={form.name}
          onChange={(e) =>
            setForm((prev) => ({ ...prev, name: e.target.value }))
          }
          placeholder="e.g. Acme Ad Network"
          required
        />
      </div>

      <div className="form-group-modal">
        <label htmlFor="provider-alias">Alias</label>
        <input
          id="provider-alias"
          value={form.alias}
          onChange={(e) =>
            setForm((prev) => ({ ...prev, alias: slugify(e.target.value) }))
          }
          placeholder="e.g. acme-network"
        />
        <p className="form-help-text">
          URL-safe identifier used in serving links (lowercase letters, numbers,
          hyphens).
        </p>
      </div>

      <div className="form-group-modal">
        <label>Type</label>
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "0.75rem",
            marginTop: "0.25rem",
          }}
        >
          {TYPE_OPTIONS.map((opt) => (
            <label
              key={opt.value}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.4rem",
                cursor: "pointer",
              }}
            >
              <input
                type="checkbox"
                checked={form.types.includes(opt.value)}
                onChange={() => toggleType(opt.value)}
              />
              <span>{opt.label}</span>
            </label>
          ))}
        </div>
      </div>

      {form.types.includes("rtb") && (
        <div className="form-group-modal">
          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: "0.4rem",
              cursor: "pointer",
            }}
          >
            <input
              type="checkbox"
              checked={form.allowTransit}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, allowTransit: e.target.checked }))
              }
            />
            <span>Allow transit campaigns</span>
          </label>
          <p className="form-help-text">
            Enables this publisher to participate as a transit endpoint.
          </p>
        </div>
      )}

      {form.types.includes("rtb") && (
        <div className="form-group-modal">
          <label htmlFor="provider-transit-buffer">
            Transit await buffer (ms)
          </label>
          <input
            id="provider-transit-buffer"
            type="number"
            min="0"
            step="1"
            value={form.settings.transitBufferMs}
            onChange={(e) =>
              setForm((prev) => ({
                ...prev,
                settings: { ...prev.settings, transitBufferMs: e.target.value },
              }))
            }
            placeholder="e.g. 200"
          />
          <p className="form-help-text">
            Optional. Maximum time the transit layer waits before forwarding the
            bid request.
          </p>
        </div>
      )}

      {form.types.includes("vast") && (
        <div className="form-group-modal">
          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: "0.4rem",
              cursor: "pointer",
            }}
          >
            <input
              type="checkbox"
              checked={form.settings.issueViewerCookie}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  settings: {
                    ...prev.settings,
                    issueViewerCookie: e.target.checked,
                  },
                }))
              }
            />
            <span>Issue web-viewer cookie (frequency capping)</span>
          </label>
          <p className="form-help-text">
            Issues a partitioned viewer cookie (fingerprint fallback) so web
            viewers can be frequency-capped. Requires the global feature flag.
          </p>
        </div>
      )}

      {(showVastUrl || showRtbUrl) && (
        <div className="form-group-modal">
          <label>Serving URLs</label>
          <div className="flex flex-col gap-2">
            {showVastUrl && (
              <ServingUrlGroup
                title="vast"
                protocol="vast"
                alias={savedAlias}
                token={savedToken}
              />
            )}
            {showRtbUrl && (
              <ServingUrlGroup
                title="rtb"
                protocol="rtb"
                alias={savedAlias}
                token={savedToken}
              />
            )}
          </div>
        </div>
      )}

      {canShowUrls && (
        <div className="form-group-modal">
          <label>Reporting URL</label>
          <div className="flex flex-col gap-2">
            <ServingUrlGroup
              title="stats"
              protocol="report/publisher"
              alias={savedAlias}
              token={savedToken}
            />
          </div>
          <p className="form-help-text">
            Daily stats (bids, impressions, owed). Append{" "}
            <code>?from=YYYY-MM-DD&amp;to=YYYY-MM-DD</code> to filter the date
            range.
          </p>
        </div>
      )}

      {canShowUrls && (
        <div className="form-group-modal">
          <label>Web pixel</label>
          <PixelEmbed alias={savedAlias} token={savedToken} />
          <p className="form-help-text">
            Measurement on the publisher&apos;s own site, resolving a device id
            shared with VAST serving — so pixel data enriches the same devices
            used for capping and targeting. Two ways to embed it: a script that
            reports the page context and the browser&apos;s device
            characteristics, or a plain image that reports whatever its own
            request carries and needs no JavaScript.
          </p>
        </div>
      )}

      <div className="form-group-modal">
        <label>Ad kinds</label>
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "0.75rem",
            marginTop: "0.25rem",
          }}
        >
          {KIND_OPTIONS.map(({ label, bit }) => (
            <label
              key={bit}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.4rem",
                cursor: "pointer",
              }}
            >
              <input
                type="checkbox"
                checked={(form.kinds & bit) !== 0}
                onChange={() => toggleKind(bit)}
              />
              <span>{label}</span>
            </label>
          ))}
        </div>
      </div>

      <div className="form-group-modal">
        <label htmlFor="provider-cpm">CPM ($)</label>
        <input
          id="provider-cpm"
          type="number"
          min="0"
          step="0.01"
          value={form.cpm}
          onChange={(e) =>
            setForm((prev) => ({
              ...prev,
              cpm: e.target.value,
            }))
          }
          placeholder="e.g. 2.50"
        />
      </div>

      <div
        style={{
          display: "flex",
          gap: "0.75rem",
          justifyContent: "flex-end",
          marginTop: "1.5rem",
        }}
      >
        <button type="button" className="btn btn-secondary" onClick={onCancel}>
          Cancel
        </button>
        <button type="submit" className="btn btn-primary">
          {initialProvider ? "Save Changes" : "Create Publisher"}
        </button>
      </div>
    </form>
  );
}
